import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { uniqBy } from "lodash";
import {
  CategoryStatusCompany,
  CategoryStatusUserSettingItem,
  CategoryStatusUserSettingPayloadItem,
  SaveCategoryStatusUserSettingsPayload,
} from "@lhd/types";
import { useSaveCategoryStatusUserSettings } from "@lhd/hooks";
import { FilterSelected } from "@lhd/components/SearchAreaBox/types";
import { FilterInfo } from "@lhd/features/Apps/AppList/ManagementAppSheet/ManagementAppSheetList/AppSheetComponents/FilterIcon";
import {
  FILTER_STATUS_TYPE,
  RECORD_TYPE,
  SCREEN_NAME,
  SPECIAL_FILTER_LABEL,
  SPECIAL_FILTER_VALUE,
} from "@lhd/constants";

export interface UseUserCategoryStatusFilterProps {
  companyId?: number;
  screen: string;
  chatStatuses: (CategoryStatusUserSettingItem | CategoryStatusCompany)[];
  chatCategories: (CategoryStatusUserSettingItem | CategoryStatusCompany)[];
  isLoadingFilters: boolean;
  statusFilterSelected: FilterSelected[];
  setStatusFilterSelected: Dispatch<SetStateAction<FilterSelected[]>>;
  categoryFilterSelected: FilterSelected[];
  setCategoryFilterSelected: Dispatch<SetStateAction<FilterSelected[]>>;
  filterInfo: FilterInfo;
  setFilterInfo: Dispatch<SetStateAction<FilterInfo>>;
  statusOptions: {
    text: string;
    value: string;
    is_auto_select?: boolean;
    id?: any;
  }[];
  categoryOptions: {
    text: string;
    value: string;
    is_auto_select?: boolean;
    id?: any;
  }[];
  statusKey?: string;
  categoryKey?: string;
  enabled?: boolean;
  hasNullStatusOption?: boolean;
}

/**
 * Hook to manage user/company personalized category/status filter settings for chat:
 * 1. On mount / data loaded: Read is_auto_select from BE to populate filter checkboxes and grid filterInfo.
 * 2. On filter change: Call PATCH /api/v1/category_status_user_settings via mutation hook with 400ms debounce.
 */
export const useUserCategoryStatusFilter = ({
  companyId,
  screen,
  chatStatuses,
  chatCategories,
  isLoadingFilters,
  statusFilterSelected,
  setStatusFilterSelected,
  categoryFilterSelected,
  setCategoryFilterSelected,
  filterInfo,
  setFilterInfo,
  statusOptions,
  categoryOptions,
  statusKey = RECORD_TYPE.STATUS,
  categoryKey = RECORD_TYPE.CATEGORY,
  enabled = true,
  hasNullStatusOption = false,
}: UseUserCategoryStatusFilterProps) => {
  const isInitializedRef = useRef(false);
  const initialStatusAutoSelectRef = useRef<Record<string | number, boolean>>(
    {},
  );
  const initialCategoryAutoSelectRef = useRef<Record<string | number, boolean>>(
    {},
  );
  const hasUserModifiedStatusRef = useRef(false);
  const hasUserModifiedCategoryRef = useRef(false);
  const isSavedRef = useRef(false);
  const statusDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const categoryDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const initialStatusSnapshotRef = useRef<string>("");
  const initialCategorySnapshotRef = useRef<string>("");

  const { mutate: saveUserSettingsMutation } =
    useSaveCategoryStatusUserSettings();

  // Keep references to latest states for handlers
  const latestRef = useRef({
    companyId,
    screen,
    chatStatuses,
    chatCategories,
    statusFilterSelected,
    categoryFilterSelected,
    statusOptions,
    categoryOptions,
  });

  useEffect(() => {
    latestRef.current = {
      companyId,
      screen,
      chatStatuses,
      chatCategories,
      statusFilterSelected,
      categoryFilterSelected,
      statusOptions,
      categoryOptions,
    };
  }, [
    companyId,
    screen,
    chatStatuses,
    chatCategories,
    statusFilterSelected,
    categoryFilterSelected,
    statusOptions,
    categoryOptions,
  ]);

  // 1. Initialize filter selection from BE data on mount / when filters are loaded
  useEffect(() => {
    if (!enabled || !companyId) return;
    if (isInitializedRef.current) return;
    if (isLoadingFilters) return;

    if (chatStatuses.length === 0 && chatCategories.length === 0) return;

    // 1.1 Process Statuses from BE
    if (chatStatuses.length > 0) {
      const hasAutoSelectedStatus = chatStatuses.some(
        (item: any) => item.is_auto_select,
      );
      const hasOverrideStatus = chatStatuses.some(
        (item: any) =>
          item.overrides?.is_auto_select !== null &&
          item.overrides?.is_auto_select !== undefined,
      );

      const initialStatusFilter: FilterSelected[] = chatStatuses
        .filter((item: any) => !item.is_hidden)
        .map((item: any) => {
          const existingItem = statusFilterSelected?.find(
            (s: any) =>
              (s.id && item.id && String(s.id) === String(item.id)) ||
              String(s.value) === String(item.name) ||
              String(s.value) === String(item.value) ||
              String((s as any).label) === String(item.name) ||
              String((s as any).label) === String(item.value),
          );

          let isChecked = false;
          if (hasAutoSelectedStatus) {
            isChecked = Boolean(item.is_auto_select);
          } else if (hasOverrideStatus) {
            // User explicitly unselected all in previous session
            isChecked = false;
          } else if (existingItem) {
            isChecked =
              existingItem.status === FILTER_STATUS_TYPE.CHECKED ||
              existingItem.status === FILTER_STATUS_TYPE.SELECTED;
          } else {
            // Default when no auto_select and no override: check all
            isChecked = true;
          }

          if (item.id) {
            initialStatusAutoSelectRef.current[item.id] = isChecked;
          }

          return {
            value: item.name,
            id: item.id,
            status: isChecked
              ? FILTER_STATUS_TYPE.CHECKED
              : FILTER_STATUS_TYPE.UNCHECKED,
            is_hidden: Boolean(item.is_hidden),
            is_auto_select: Boolean(item.is_auto_select),
          };
        });

      setStatusFilterSelected(initialStatusFilter);
      initialStatusSnapshotRef.current = JSON.stringify(
        initialStatusFilter.map((x) => ({ value: x.value, status: x.status })),
      );

      // Sync into filterInfo
      const checkedValues = initialStatusFilter
        .filter(
          (x) =>
            x.status === FILTER_STATUS_TYPE.CHECKED ||
            x.status === FILTER_STATUS_TYPE.SELECTED,
        )
        .map((x) => x.value);
      const isCheckAll =
        initialStatusFilter.length > 0 &&
        checkedValues.length === initialStatusFilter.length;

      setFilterInfo((prev: any) => ({
        ...prev,
        filter: {
          ...(prev.filter || {}),
          [statusKey]: isCheckAll
            ? {
                filterOptions: statusOptions,
                isCheckAll: true,
              }
            : {
                filter: checkedValues,
                filterOptions: statusOptions,
                isCheckAll: false,
              },
        },
      }));
    }

    // 1.2 Process Categories from BE
    if (chatCategories.length > 0) {
      const hasAutoSelectedCategory = chatCategories.some(
        (item: any) => item.is_auto_select,
      );
      const hasOverrideCategory = chatCategories.some(
        (item: any) =>
          item.overrides?.is_auto_select !== null &&
          item.overrides?.is_auto_select !== undefined,
      );

      const initialCategoryFilter: FilterSelected[] = chatCategories
        .filter((item: any) => !item.is_hidden)
        .map((item: any) => {
          const existingItem = categoryFilterSelected?.find(
            (c: any) =>
              (c.id && item.id && String(c.id) === String(item.id)) ||
              String(c.value) === String(item.name) ||
              String(c.value) === String(item.value) ||
              String((c as any).label) === String(item.name) ||
              String((c as any).label) === String(item.value),
          );

          let isChecked = false;
          if (hasAutoSelectedCategory) {
            isChecked = Boolean(item.is_auto_select);
          } else if (hasOverrideCategory) {
            // User explicitly unselected all in previous session
            isChecked = false;
          } else if (existingItem) {
            isChecked =
              existingItem.status === FILTER_STATUS_TYPE.CHECKED ||
              existingItem.status === FILTER_STATUS_TYPE.SELECTED;
          } else {
            // Default when no auto_select and no override: check all
            isChecked = true;
          }

          if (item.id) {
            initialCategoryAutoSelectRef.current[item.id] = isChecked;
          }

          return {
            value: item.name,
            id: item.id,
            status: isChecked
              ? FILTER_STATUS_TYPE.CHECKED
              : FILTER_STATUS_TYPE.UNCHECKED,
            is_hidden: Boolean(item.is_hidden),
            is_auto_select: Boolean(item.is_auto_select),
          };
        });

      setCategoryFilterSelected(initialCategoryFilter);
      initialCategorySnapshotRef.current = JSON.stringify(
        initialCategoryFilter.map((x) => ({
          value: x.value,
          status: x.status,
        })),
      );

      // Sync into filterInfo
      const checkedCategoryValues = initialCategoryFilter
        .filter(
          (x) =>
            x.status === FILTER_STATUS_TYPE.CHECKED ||
            x.status === FILTER_STATUS_TYPE.SELECTED,
        )
        .map((x) => x.value);
      const isCheckAllCategory =
        initialCategoryFilter.length > 0 &&
        checkedCategoryValues.length === initialCategoryFilter.length;

      setFilterInfo((prev: any) => ({
        ...prev,
        filter: {
          ...(prev.filter || {}),
          [categoryKey]: isCheckAllCategory
            ? {
                filterOptions: categoryOptions,
                isCheckAll: true,
              }
            : {
                filter: checkedCategoryValues,
                filterOptions: categoryOptions,
                isCheckAll: false,
              },
        },
      }));
    }

    isInitializedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    companyId,
    isLoadingFilters,
    chatStatuses,
    chatCategories,
    statusOptions,
    categoryOptions,
    statusKey,
    categoryKey,
    hasNullStatusOption,
    screen,
    setStatusFilterSelected,
    setCategoryFilterSelected,
    setFilterInfo,
  ]);

  // 2. Save status settings via mutation hook
  const saveStatusSettings = useCallback(() => {
    const {
      companyId: cId,
      screen: scr,
      chatStatuses: statuses,
      statusFilterSelected: curStatusFilter,
    } = latestRef.current;

    if (!cId || !isInitializedRef.current || statuses.length === 0) return;

    const statusSettings: CategoryStatusUserSettingPayloadItem[] = [];

    statuses.forEach((item: any) => {
      if (!item.id) return;

      const curItem = curStatusFilter.find(
        (s: any) =>
          (s.id && item.id && String(s.id) === String(item.id)) ||
          String(s.value) === String(item.name) ||
          String(s.value) === String(item.value) ||
          (s.label && String(s.label) === String(item.name)) ||
          (s.label && String(s.label) === String(item.value)) ||
          ((item.name === SPECIAL_FILTER_LABEL.NULL ||
            item.value === SPECIAL_FILTER_VALUE.NULL) &&
            (s.value === SPECIAL_FILTER_VALUE.NULL ||
              s.value === SPECIAL_FILTER_LABEL.NULL ||
              s.text === SPECIAL_FILTER_LABEL.NULL ||
              s.label === SPECIAL_FILTER_LABEL.NULL)),
      );

      const isChecked = curItem
        ? curItem.status === FILTER_STATUS_TYPE.CHECKED ||
          curItem.status === FILTER_STATUS_TYPE.SELECTED
        : Boolean(item.is_auto_select);

      statusSettings.push({
        category_status_company_id: Number(item.id),
        is_auto_select: isChecked,
      });
    });

    const uniqueSettings = uniqBy(statusSettings, "category_status_company_id");
    if (uniqueSettings.length === 0) return;

    const payload: SaveCategoryStatusUserSettingsPayload = {
      company_id: cId,
      screen: scr === SCREEN_NAME.LIST_MESS ? SCREEN_NAME.CHAT : scr,
      record_type: RECORD_TYPE.STATUS,
      settings: uniqueSettings,
    };

    saveUserSettingsMutation(
      { payload },
      {
        onSuccess: () => {
          hasUserModifiedStatusRef.current = false;
          isSavedRef.current = true;
        },
        onError: (err) => {
          console.error("Failed to auto-save status user settings", err);
        },
      },
    );
  }, [saveUserSettingsMutation]);

  // 3. Save category settings via mutation hook
  const saveCategorySettings = useCallback(() => {
    const {
      companyId: cId,
      screen: scr,
      chatCategories: categories,
      categoryFilterSelected: curCategoryFilter,
    } = latestRef.current;

    if (!cId || !isInitializedRef.current || categories.length === 0) return;

    const categorySettings: CategoryStatusUserSettingPayloadItem[] = [];

    categories.forEach((item: any) => {
      if (!item.id) return;

      const curItem = curCategoryFilter.find(
        (c: any) =>
          (c.id && item.id && String(c.id) === String(item.id)) ||
          String(c.value) === String(item.name) ||
          String(c.value) === String(item.value) ||
          (c.label && String(c.label) === String(item.name)) ||
          (c.label && String(c.label) === String(item.value)) ||
          ((item.name === SPECIAL_FILTER_LABEL.NULL ||
            item.value === SPECIAL_FILTER_VALUE.NULL) &&
            (c.value === SPECIAL_FILTER_VALUE.NULL ||
              c.value === SPECIAL_FILTER_LABEL.NULL ||
              c.text === SPECIAL_FILTER_LABEL.NULL ||
              c.label === SPECIAL_FILTER_LABEL.NULL)),
      );

      const isChecked = curItem
        ? curItem.status === FILTER_STATUS_TYPE.CHECKED ||
          curItem.status === FILTER_STATUS_TYPE.SELECTED
        : Boolean(item.is_auto_select);

      categorySettings.push({
        category_status_company_id: Number(item.id),
        is_auto_select: isChecked,
      });
    });

    const uniqueSettings = uniqBy(
      categorySettings,
      "category_status_company_id",
    );
    if (uniqueSettings.length === 0) return;

    const payload: SaveCategoryStatusUserSettingsPayload = {
      company_id: cId,
      screen: scr === SCREEN_NAME.LIST_MESS ? SCREEN_NAME.CHAT : scr,
      record_type: RECORD_TYPE.CATEGORY,
      settings: uniqueSettings,
    };

    saveUserSettingsMutation(
      { payload },
      {
        onSuccess: () => {
          hasUserModifiedCategoryRef.current = false;
          isSavedRef.current = true;
        },
        onError: (err) => {
          console.error("Failed to auto-save category user settings", err);
        },
      },
    );
  }, [saveUserSettingsMutation]);

  // 4. Debounced auto-save on status selection change
  useEffect(() => {
    if (!isInitializedRef.current) return;
    const currentSnapshot = JSON.stringify(
      statusFilterSelected.map((x) => ({ value: x.value, status: x.status })),
    );
    if (
      initialStatusSnapshotRef.current &&
      currentSnapshot === initialStatusSnapshotRef.current
    ) {
      return;
    }

    hasUserModifiedStatusRef.current = true;
    isSavedRef.current = false;

    if (statusDebounceTimerRef.current) {
      clearTimeout(statusDebounceTimerRef.current);
    }
    statusDebounceTimerRef.current = setTimeout(() => {
      saveStatusSettings();
      initialStatusSnapshotRef.current = currentSnapshot;
    }, 400);

    return () => {
      if (statusDebounceTimerRef.current) {
        clearTimeout(statusDebounceTimerRef.current);
      }
    };
  }, [statusFilterSelected, saveStatusSettings]);

  // 5. Debounced auto-save on category selection change
  useEffect(() => {
    if (!isInitializedRef.current) return;
    const currentSnapshot = JSON.stringify(
      categoryFilterSelected.map((x) => ({
        value: x.value,
        status: x.status,
      })),
    );
    if (
      initialCategorySnapshotRef.current &&
      currentSnapshot === initialCategorySnapshotRef.current
    ) {
      return;
    }

    hasUserModifiedCategoryRef.current = true;
    isSavedRef.current = false;

    if (categoryDebounceTimerRef.current) {
      clearTimeout(categoryDebounceTimerRef.current);
    }
    categoryDebounceTimerRef.current = setTimeout(() => {
      saveCategorySettings();
      initialCategorySnapshotRef.current = currentSnapshot;
    }, 400);

    return () => {
      if (categoryDebounceTimerRef.current) {
        clearTimeout(categoryDebounceTimerRef.current);
      }
    };
  }, [categoryFilterSelected, saveCategorySettings]);

  // 6. Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (statusDebounceTimerRef.current) {
        clearTimeout(statusDebounceTimerRef.current);
      }
      if (categoryDebounceTimerRef.current) {
        clearTimeout(categoryDebounceTimerRef.current);
      }
    };
  }, []);
};
